import { createBrand, updateBrand, useBrand } from '@gozoki/api'
import { useBrandForm } from '@gozoki/api/src/forms/useBrandForm'
import { Button, IconButton, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'
import z from 'zod/v3'
import LoadingPaper from '../../../components/containers/LoadingPaper'
import FileInputRhf from '../../../components/inputs/FileInputRhf'
import TextFieldRhf from '../../../components/inputs/TextFieldRhf'
import Page from '../../../components/Page'
import { useRights } from '../../../utils/hooks'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

import { withResourcePermission } from '../../../components/RequireResourcePermission'
export const Route = createFileRoute('/_authenticated/categories/brand/new')({
  component: withResourcePermission('categories', 'read')(AddBrandPage),
  validateSearch: z.object({
    brandId: z.number().optional(),
  }),
})

function AddBrandPage() {
  const navigate = Route.useNavigate()
  const { brandId } = Route.useSearch()

  const isUserAdmin = useRights(['ADMIN'])

  const updateMode = !!brandId // Update mode if there is a brand ID in the URL

  // If this is update mode, use isLoading to show a loading indicator before the data arrives
  const { data, isLoading } = useBrand(brandId)

  const { control, trigger, getValues, setValue } = useBrandForm()

  // Fill the form with initial data once the data is fetched
  useEffect(() => {
    if (data?.name) {
      setValue('name', data?.name)
    }
  }, [setValue, data?.name])

  const navigateBack = () => navigate({ search: { tab: 'brands' }, to: '/categories' })

  const { mutate: upload, isPending: isUploading } = useMutation({
    mutationFn: createBrand,
    onSuccess: navigateBack,
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: updateBrand,
    onSuccess: navigateBack,
  })

  const uploadBrand = async () => {
    const valid = await trigger() // Trigger form validation

    if (valid) {
      upload(getValues())
    }
    // If invalid, the invalid fields will be outlined with red
  }

  const uploadAndUpdateBrand = async () => {
    const valid = await trigger('name') // No need to check for image, which cannot be deleted

    if (valid && brandId) {
      update({
        data: getValues(),
        id: brandId,
      })
    }
  }

  return (
    <Page sx={{ margin: 'auto', maxWidth: '50vw' }}>
      <div style={{ alignItems: 'center', display: 'flex' }}>
        <IconButton onClick={navigateBack}>
          <ArrowLeft
            style={{
              color: ThemeColors.BLACK,
              height: '24px',
              width: '24px',
            }}
          />
        </IconButton>
        <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
          {updateMode ? `${isUserAdmin ? 'Modifier' : 'Voir'} une marque` : 'Ajouter une marque'}
        </Typography>

        <Button
          variant="outlined"
          sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
          onClick={navigateBack}
        >
          Annuler
        </Button>
        <Button
          variant="contained"
          disabled={!isUserAdmin ? true : isUpdating || isUploading}
          onClick={updateMode ? uploadAndUpdateBrand : uploadBrand}
        >
          {updateMode ? 'Modifier' : 'Ajouter'}
        </Button>
      </div>
      <LoadingPaper
        sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
        loading={isLoading && updateMode}
      >
        <Typography mb="8px" style={fonts.inputHeader}>
          Nom
        </Typography>
        <TextFieldRhf
          variant="outlined"
          label="Nom de la marque"
          color="info"
          control={control}
          trigger={trigger}
          name="name"
          fullWidth
        />
        <Typography mt="24px" mb="8px" style={fonts.inputHeader}>
          Icône ou image
        </Typography>
        <FileInputRhf
          control={control}
          name="image"
          label="Ajouter une image"
          defaultName={data?.image?.name}
        />
      </LoadingPaper>
    </Page>
  )
}
